import { Field, Form, Formik } from 'formik'
import React, { Fragment, useMemo, useRef, useCallback, useState } from 'react'
import TextField from 'components/Common/TextField'
import { AccountFormLayout } from 'components/Common/AccountFormLayout'
import { Grid, Typography, Box } from '@mui/material'
import CustomCheckbox from './CustomCheckbox'
import * as yup from 'yup'
import UserContent from './UserContent'
import { store } from 'reduxStore'
import ZendeskLink from 'components/Common/ZendeskLink'
import api from 'api'
import BusinessFormControls from './BusinessFormControls'

const sx = {
  row: {
    position: 'relative',
    marginBottom: 20
  },
  checkbox: {
    position: 'absolute',
    top: 0,
    right: -15

  },
  blockHeader: {
    marginBottom: theme => theme.spacing(2),
    fontWeight: 'bold',
    fontSize: 18,
    color: '#0076c5',
    fontFamily: 'popins',

  },
  textInputRoot: {
    marginTop: 30
  },
  switcherCustom: {
    opacity: '0.4'
  }
}

const schema = yup.object({
  legal_name: yup.string().notRequired(),
  foreign: yup.string().notRequired(),
  corp_emp: yup.string().notRequired(),
  local_emp: yup.string().notRequired(),
  legal_sub_parent_name: yup.string().notRequired(),
  public: yup.string().notRequired(),
  revenue_corporate: yup.string().notRequired(),
  revenue_local: yup.string().notRequired(),
  crs: yup.string().notRequired(),
  crc: yup.string().notRequired(),
  legalName: yup.boolean().required(),
  displayForeign: yup.boolean().required(),
  corpEmp: yup.boolean().required(),
  localEmp: yup.boolean().required(),
  legalSubParentName: yup.boolean().required(),
  displayPublic: yup.boolean().required(),
  revenueCorporate: yup.boolean().required(),
  revenueLocal: yup.boolean().required(),
  creditScore: yup.boolean().required(),
  creditClass: yup.boolean().required(),
})

const emptyValue = {
  legal_name: '',
  foreign: '',
  corp_emp: '',
  local_emp: '',
  legal_sub_parent_name: '',
  public: '',
  revenue_corporate: '',
  revenue_local: '',
  crs: '',
  crc: '',
  legalName: false,
  displayForeign: false,
  corpEmp: false,
  localEmp: false,
  legalSubParentName: false,
  displayPublic: false,
  revenueCorporate: false,
  revenueLocal: false,
  creditScore: false,
  creditClass: false,
}


const formatRevenue = (number) => {
  return parseInt(`${number}000`).toLocaleString('en-US')
}


const BusinessAttributes = ({ business, setBusiness }) => {
  const formik = useRef(null)
  const user = store.getState().userReducer.user
  const [switcherLoading, setSwitcherLoading] = useState('')



  const initialValues = useMemo(() => {
    if (business === null) return emptyValue
    return {
      legal_name: business.legal_name,
      foreign: business.foreign,
      corp_emp: business.corp_emp ? business.corp_emp.toString() : '',
      local_emp: business.local_emp ? business.local_emp.toString() : '',
      legal_sub_parent_name: business.legal_sub_parent_name,
      public: business.public,
      revenue_corporate: business.revenue_corporate || '',
      revenue_local: business.revenue_local || '',
      crs: business.crs ? business.crs.toString() : '',
      crc: business.crc ? business.crc.toString() : '',
      legalName: business.details.displayedFields.legalName,
      displayForeign: business.details.displayedFields.foreign,
      corpEmp: business.details.displayedFields.corpEmp,
      localEmp: business.details.displayedFields.localEmp,
      legalSubParentName: business.details.displayedFields.legalSubParentName,
      displayPublic: business.details.displayedFields.public,
      revenueCorporate: business.details.displayedFields.revenueCorporate,
      revenueLocal: business.details.displayedFields.revenueLocal,
      creditScore: business.details.displayedFields.creditScore,
      creditClass: business.details.displayedFields.creditClass
    }
  }, [business])

  const handleInputChange = useCallback(async (body) => {
    if (business) {
      setSwitcherLoading(body.fieldName)
      try {
        let updatedFieldName = body.fieldName
        if (body.fieldName === 'displayPublic') {
          updatedFieldName = 'public'
        }
        if (body.fieldName === 'displayForeign') {
          updatedFieldName = 'foreign'
        }
        await api.toggleDisplayField({ ...body, fieldName: updatedFieldName })

        const businessToUpdate = {
          ...business,
          details: {
            ...business.details,
            displayedFields: {
              ...business.details.displayedFields,
              [updatedFieldName]: body.display,
            }
          }
        }
        setBusiness(businessToUpdate)
      } catch (error) {
        console.log(error)
      } finally {
        setSwitcherLoading('')
      }
    }
  }, [business, setBusiness])


  if (user?.kybValidated === undefined) {
    return (
      <UserContent
        label={(user.accountType === 'free') ? 'Subscribe' : 'Find Now'}
        content={
          (user.accountType === 'free')
            ? 'Become a premium member to claim your business, add additional fields on your profile and monitor businesses you are interested in.'
            : 'Find your business to claim it, add additional fields and enhance your profile for suppliers, customers and partners.'
        }
      />
    )
  }


  return (
    <>
      <Formik
        innerRef={formik}
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={() => { }}
        enableReinitialize
      >
        <Form style={{ flex: 1 }}>
          <BusinessFormControls user={user} model={business} />
          <AccountFormLayout
            leftSide={
              <Fragment>
                <Grid container style={{ marginTop: 40 }} classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="legal_name">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Legal Business Name"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="legalName">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="foreign">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="US or Foreign Owned"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          value={field.value ? 'Foreign' : 'US'}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box className={sx.checkbox}>
                    <Field name="displayForeign">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid container style={{ marginTop: 60 }} classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="legal_sub_parent_name">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Legal Subsidiary - Parent Name"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="legalSubParentName">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="public">
                      {({ field, meta }) => {
                        let publicValue = 'No'
                        return (
                          <TextField
                            {...field}
                            label="Public Company"
                            placeholder=""
                            fullWidth
                            borderLess
                            error={Boolean(meta.touched && meta.error)}
                            helperText={meta.touched && meta.error}
                            disabled
                            externalClasses={{ root: sx.textInputRoot }}
                            value={publicValue}
                            disableOnlyInput
                          />
                        )
                      }}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="displayPublic">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Fragment>
            }
            center={
              <Fragment>
                <Typography sx={{ fontWeight: 'bold', marginBottom: '20px', fontSize: '16px', color: '#0076c5' }}>Estimated Number of Employees</Typography>
                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="local_emp">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="This Location"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          disableOnlyInput
                        />
                      )}

                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="localEmp">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="corp_emp">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Corporate"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="corpEmp">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Typography sx={{ fontWeight: 'bold', marginBottom: '20px', fontSize: '16px', color: '#0076c5' }}>Estimated Annual Revenue</Typography>
                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="revenue_local">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="This Location"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          value={`$${formatRevenue(field.value)}`}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="revenueLocal">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="revenue_corporate">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Corporate"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          value={`$${formatRevenue(field.value)}`}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="revenueCorporate">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Fragment>
            }
            rightSideFullSize
            hideDividers
            rightSide={
              <Fragment>
                <Typography sx={{ fontWeight: 'bold', marginBottom: '20px', fontSize: '16px', color: '#0076c5' }}>Business Health Metrics</Typography>
                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="crs">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Business Credit Risk Score"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="creditScore">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
                <Grid container classes={{ root: sx.row }} alignItems="center" justifyContent="space-between" className='customAuthInput2'>
                  <Grid item xs={12}>
                    <Field name="crc">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          label="Business Credit Risk Class"
                          placeholder=""
                          fullWidth
                          borderLess
                          error={Boolean(meta.touched && meta.error)}
                          helperText={meta.touched && meta.error}
                          disabled
                          externalClasses={{ root: sx.textInputRoot }}
                          disableOnlyInput
                        />
                      )}
                    </Field>
                  </Grid>
                  <Box sx={sx.checkbox}>
                    <Field name="creditClass">
                      {({ field, meta }) => (
                        <div style={{ position: 'relative' }}>
                          <CustomCheckbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e, checked) => {
                              business && handleInputChange({ businessId: business.ext_id, display: checked, fieldName: field.name })
                            }}
                            customClasses={{ labelRoot: (switcherLoading === field.name) ? sx.switcherCustom : '' }}
                          />
                        </div>
                      )}
                    </Field>
                  </Box>
                </Grid>
              </Fragment>
            }
          />
        </Form>
      </Formik>
      <div>
        <ZendeskLink />
      </div>
    </>
  )
}

export default BusinessAttributes